import { UserManager, Log } from 'oidc-client-ts'
import { jwtDecode } from 'jwt-decode'

// Enable logging for development
if (import.meta.env.DEV) {
	Log.setLogger(console)
	Log.setLevel(Log.DEBUG)
}

function isAndroid () {
	return typeof (window as any).AndroidNative !== 'undefined'
}

function isIOS () {
	return (
		typeof (window as any).webkit?.messageHandlers?.iOSNative !== 'undefined'
	)
}

export function isMobileBridge () {
	return isAndroid() || isIOS()
}

class AuthService {
	private token: string | null = null
	private refreshTimer: number | null = null
	private pendingPromise: Promise<string> | null = null
	private userManager: UserManager | null = null

	private MAX_RETRIES = 5
	private RETRY_DELAY = 800

	constructor () {
		if (!isMobileBridge()) {
			this.initOidc()
		}
	}

	// ----------------------------
	// WEB OIDC
	// ----------------------------

	private initOidc () {
		this.userManager = new UserManager({
			authority: import.meta.env.VITE_OIDC_AUTHORITY,
			client_id: import.meta.env.VITE_OIDC_CLIENT_ID,
			client_secret: 'telemedicalSecret',

			redirect_uri: window.location.origin + '/signin-callback',
			post_logout_redirect_uri: window.location.origin,

			automaticSilentRenew: true,

			response_type: 'code',

			scope:
				'openid profile patient meuc.domainsTelemedicine.read meuc.domainsTelemedicine.write meuc.sharedCollections.item.read meuc.sharedCollections.type.read'
		})

		this.userManager.events.addSilentRenewError((error: any) => {
			console.warn('Silent renew failed', error)

			this.handleSilentRenewError()
		})

		if (import.meta.env.DEV) {
			this.userManager.events.addUserLoaded((user: any) => {
				console.log('OIDC user loaded', user)
			})
		}

		this.userManager.events.addAccessTokenExpired(() => {
			console.warn('Access token expired')
			this.userManager?.signinRedirect()
		})

		this.userManager.events.addAccessTokenExpiring(() => {
			console.log('Access token expiring')
		})
	}

	private async handleSilentRenewError () {
		try {
			const user = await this.userManager?.getUser()
			if (user && !user.expired) {
				return
			}
			await this.userManager?.signinRedirect()
		} catch (error) {
			console.error('Error handling silent renew:', error)
		}
	}

	// ----------------------------
	// MOBILE TOKEN REQUEST
	// ----------------------------

	private async getTokenFromNative (): Promise<string> {
		if (this.pendingPromise) {
			return this.pendingPromise
		}

		this.pendingPromise = new Promise((resolve, reject) => {
			const callbackName = '__nativeTokenCallback'

			;(window as any)[callbackName] = (token: string, error: any) => {
				delete (window as any)[callbackName]
				this.pendingPromise = null

				if (error || !token) {
					reject(error ?? new Error('Native token fetch failed'))
					return
				}

				this.token = token
				this.scheduleMobileRefresh(token)

				resolve(token)
			}

			if (isAndroid()) {
				;(window as any).AndroidNative.getAccessTokenAsync(callbackName)
				return
			}

			if (isIOS()) {
				;(window as any).webkit.messageHandlers.iOSNative.postMessage({
					action: 'getAccessToken',
					callbackName
				})
				return
			}

			reject(new Error('Unknown native platform'))
		})

		return this.pendingPromise
	}

	// ----------------------------
	// MOBILE RETRY
	// ----------------------------

	private async getTokenWithRetry (): Promise<string> {
		let lastError: any = null

		for (let i = 0; i < this.MAX_RETRIES; i++) {
			try {
				return await this.getTokenFromNative()
			} catch (err) {
				lastError = err
				await new Promise((r) => setTimeout(r, this.RETRY_DELAY))
			}
		}

		throw lastError
	}

	// ----------------------------
	// MOBILE TOKEN REFRESH
	// ----------------------------

	private scheduleMobileRefresh (token: string) {
		const payload = jwtDecode(token)

		if (!payload?.exp) return

		const expiresAt = payload.exp * 1000
		const now = Date.now()

		// refresh 60s before expiration
		const refreshIn = expiresAt - now - 60000

		if (this.refreshTimer) {
			clearTimeout(this.refreshTimer)
		}

		if (refreshIn <= 0) {
			this.refreshMobileToken()
			return
		}

		this.refreshTimer = window.setTimeout(() => {
			this.refreshMobileToken()
		}, refreshIn)
	}

	private async refreshMobileToken () {
		try {
			console.log('Refreshing mobile token')

			const newToken = await this.getTokenFromNative()

			this.token = newToken
			this.scheduleMobileRefresh(newToken)
		} catch (error) {
			console.error('Mobile token refresh failed', error)

			try {
				sessionStorage.setItem('euc-telemedicine-pending-error', JSON.stringify({
					message: error instanceof Error ? error.message : String(error)
				}))
			} finally {
				window.location.href = '/error'
			}
		}
	}

	// ----------------------------
	// PUBLIC API
	// ----------------------------

	async signIn () {
		if (!isMobileBridge()) {
			return this.userManager?.signinRedirect()
		}
	}

	async signOut () {
		if (!isMobileBridge()) {
			return this.userManager?.signoutRedirect()
		}
	}

	async getAccessToken (): Promise<string | null> {
		if (isMobileBridge()) {
			if (this.token) {
				return this.token
			}

			return this.getTokenWithRetry()
		}

		const user = await this.userManager?.getUser()
		return user?.access_token || null
	}

	get isAuthenticated (): boolean {
		if (isMobileBridge()) {
			return !!this.token
		}

		// For web, we need to check if user exists and is not expired
		// This is async, so we'll need to handle it differently in actual usage
		return false
	}

	get oidcInstance () {
		return {
			mgr: this.userManager,
			authName: 'oidc',
			signOut: () => this.signOut()
		}
	}
}

export const authService = new AuthService()
